import React, { useEffect, useRef } from 'react';
import styles from './HeroSlider.module.css';
import { ensureBannerFont } from '../../lib/bannerFont';

interface HeroSliderProps {
  title: string;
  subtitle: string;
  imageUrl?: string;
  contentPosition?: 'left' | 'center' | 'right';
  contentVerticalAlign?: 'top' | 'middle' | 'bottom';
  contentHorizontalOffset?: number;
  contentVerticalOffset?: number;
  videoUrl?: string;
  isVideo?: boolean;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  showContent?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonBgColor?: string;
  buttonTextColor?: string;
  buttonRadius?: number;
  fontFamily?: string;
  overlayOpacity?: number;
  backgroundColor?: string;
}

const HeroSlider = ({
  title,
  subtitle,
  imageUrl,
  contentPosition = 'left',
  contentVerticalAlign = 'middle',
  contentHorizontalOffset = 0,
  contentVerticalOffset = 0,
  videoUrl,
  isVideo = false,
  autoplay = true,
  muted = true,
  loop = true,
  showContent = true,
  buttonText,
  buttonLink,
  buttonBgColor,
  buttonTextColor,
  buttonRadius,
  fontFamily,
  overlayOpacity,
  backgroundColor
}: HeroSliderProps) => {
  useEffect(() => { ensureBannerFont(fontFamily); }, [fontFamily]);

  const videoRef = useRef<HTMLVideoElement>(null);
  
  // Izračunavanje pozicionog stila za heroContent div
  const getContentPositionStyle = () => {
    const positionStyle: React.CSSProperties = {
      position: 'absolute',
    };
    
    // Horizontalna pozicija
    switch (contentPosition) {
      case 'left':
        positionStyle.left = `${20 + contentHorizontalOffset}px`;
        break;
      case 'center':
        positionStyle.left = '50%';
        positionStyle.transform = 'translateX(-50%)';
        if (contentHorizontalOffset) {
          positionStyle.marginLeft = `${contentHorizontalOffset}px`;
        }
        break;
      case 'right':
        positionStyle.right = `${20 + contentHorizontalOffset}px`;
        break;
    }
    
    // Vertikalna pozicija
    switch (contentVerticalAlign) {
      case 'top':
        positionStyle.top = `${50 + contentVerticalOffset}px`;
        break;
      case 'middle':
        positionStyle.top = '50%';
        positionStyle.transform = positionStyle.transform 
          ? 'translate(-50%, -50%)' 
          : 'translateY(-50%)';
        if (contentVerticalOffset) {
          positionStyle.marginTop = `${contentVerticalOffset}px`;
        }
        break;
      case 'bottom':
        positionStyle.bottom = `${50 + contentVerticalOffset}px`;
        break;
    }
    
    return positionStyle;
  };

  // Funkcija za proveru tipa video URL-a
  const getVideoType = (url: string) => {
    if (!url) return 'unknown';
    
    // YouTube URL
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      return 'youtube';
    }
    
    // Vimeo URL
    if (url.includes('vimeo.com')) {
      return 'vimeo';
    }
    
    // Direktan video URL
    if (url.match(/\.(mp4|webm|ogg|mov|avi)(\?.*)?$/i)) {
      return 'direct';
    }
    
    // Nepoznat tip, probamo kao direktan
    return 'direct';
  };
  
  // Funkcija za dobijanje embed URL-a za YouTube
  const getYouTubeEmbedUrl = (url: string) => {
    // Izvlačimo ID videa iz YouTube URL-a
    let videoId = '';
    
    if (url.includes('youtube.com/watch')) {
      const urlParams = new URLSearchParams(new URL(url).search);
      videoId = urlParams.get('v') || '';
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1].split('?')[0];
    }
    
    if (!videoId) return null;
    
    // Kreiramo embed URL sa parametrima
    return `https://www.youtube.com/embed/${videoId}?autoplay=${autoplay ? 1 : 0}&mute=${muted ? 1 : 0}&loop=${loop ? 1 : 0}&playlist=${videoId}&controls=0&showinfo=0&rel=0`;
  };
  
  // Funkcija za dobijanje embed URL-a za Vimeo
  const getVimeoEmbedUrl = (url: string) => {
    // Izvlačimo ID videa iz Vimeo URL-a
    const vimeoRegex = /vimeo\.com\/(?:video\/)?(\d+)/;
    const match = url.match(vimeoRegex);
    const videoId = match ? match[1] : null;
    
    if (!videoId) return null;
    
    // Kreiramo embed URL sa parametrima
    return `https://player.vimeo.com/video/${videoId}?autoplay=${autoplay ? 1 : 0}&muted=${muted ? 1 : 0}&loop=${loop ? 1 : 0}&background=1`;
  };
  
  // Renderovanje video elementa na osnovu tipa URL-a
  const renderVideo = () => {
    if (!videoUrl || !isVideo) return null;
    
    const videoType = getVideoType(videoUrl);
    console.log("Video tip:", videoType, "URL:", videoUrl);
    
    switch (videoType) {
      case 'youtube': {
        const youtubeEmbedUrl = getYouTubeEmbedUrl(videoUrl);
        if (!youtubeEmbedUrl) return null;
        
        return (
          <div className={styles.videoContainer}>
            <iframe
              className={styles.backgroundVideo}
              src={youtubeEmbedUrl}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={title}
            ></iframe>
          </div>
        );
      }
      
      case 'vimeo': {
        const vimeoEmbedUrl = getVimeoEmbedUrl(videoUrl);
        if (!vimeoEmbedUrl) return null;
        
        return (
          <div className={styles.videoContainer}>
            <iframe
              className={styles.backgroundVideo}
              src={vimeoEmbedUrl}
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title={title}
            ></iframe>
          </div>
        );
      }
      
      case 'direct':
      default: {
        return (
          <div className={styles.videoContainer}>
            <video 
              ref={videoRef}
              className={styles.backgroundVideo}
              autoPlay={autoplay}
              muted={muted}
              loop={loop}
              playsInline
            >
              <source src={videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        );
      }
    }
  };

  // Efekat za logovanje video elementa
  useEffect(() => {
    if (isVideo && videoUrl) {
      console.log("Video baner:", { isVideo, videoUrl, autoplay, muted, loop });
    }
  }, [isVideo, videoUrl, autoplay, muted, loop]);

  const btnStyle: React.CSSProperties = { textDecoration: 'none', display: 'inline-block' };
  if (buttonBgColor) btnStyle.backgroundColor = buttonBgColor;
  if (buttonTextColor) btnStyle.color = buttonTextColor;
  if (buttonRadius != null) btnStyle.borderRadius = `${buttonRadius}px`;

  // "Boja pozadine" + "Providnost" stilizuju panel IZA TEKSTA (heroContent),
  // ne ceo baner. Spajamo boju i providnost u rgba i njime menjamo podrazumevani
  // background panela (CSS rgba(0,0,0,0.5)).
  const hexToRgba = (hex: string, a: number): string => {
    const h = hex.replace('#', '');
    const f = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const r = parseInt(f.slice(0, 2), 16) || 0;
    const g = parseInt(f.slice(2, 4), 16) || 0;
    const b = parseInt(f.slice(4, 6), 16) || 0;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };
  const contentStyle: React.CSSProperties = {
    ...getContentPositionStyle(),
    ...(fontFamily ? { fontFamily } : {}),
  };
  if (backgroundColor || overlayOpacity != null) {
    const a = overlayOpacity != null ? overlayOpacity : 0.5;
    contentStyle.background = hexToRgba(backgroundColor || '#000000', a);
    // Potpuno transparentno = bez panela: skloni i blur/efekat pozadine.
    if (a === 0) {
      contentStyle.backdropFilter = 'none';
      (contentStyle as any).WebkitBackdropFilter = 'none';
    }
  }

  return (
    <section className={styles.heroSlider}>
      {!isVideo && imageUrl && (
        <img
          src={imageUrl}
          alt={title}
          className={styles.heroImage}
          fetchPriority="high"
          decoding="async"
        />
      )}
      {isVideo ? renderVideo() : null}
      <div className={styles.overlay}></div>
      <div className={styles.container}>
        {showContent && (
          <div
            className={styles.heroContent}
            style={contentStyle}
          >
            <h1>{title}</h1>
            <p>{subtitle}</p>
            <a
              href={buttonLink || '/products'}
              className={styles.shopNowBtn}
              style={btnStyle}
            >
              {buttonText || 'Shop Now'}
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroSlider; 